import React, { useState } from 'react';
import { Trash2, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ROLES, POSITION_OPTIONS } from '../../../constants'; // Make sure POSITION_OPTIONS is imported here or passed as prop
import { showSuccessToast, showErrorToast } from '../../../utils/toastUtils';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal';

const ParticipantsRACIMatrix = ({
  workshops = [], // Default to empty array
  // positionOptions = [], // Use imported constant instead
  participants = [], // Default to empty array
  setParticipants,
  matrix = {}, // Default to empty object
  setMatrix,
  isTestModeActive // Receive mode status
}) => {
  const { t } = useTranslation();

  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    participantId: null,
    participantName: ''
  });

  // Create translated ROLES array
  const translatedRoles = [
    { value: "", label: t('context.fields.selectRole') },
    { value: "-", label: t('context.raci.notInvolved') },
    { value: "R", label: `R - ${t('context.raci.responsible')}` },
    { value: "A", label: `A - ${t('context.raci.accountable')}` },
    { value: "C", label: `C - ${t('context.raci.consulted')}` },
    { value: "I", label: `I - ${t('context.raci.informed')}` }
  ];

  // Add participant is now handled in Context.js to coordinate state updates

  const handleRemoveParticipant = (id) => {
    if (isTestModeActive) {
        showErrorToast(t('context.raci.deleteNotAllowedTestMode'));
        return;
    }
    if (participants.length <= 1) {
        showErrorToast(t('context.raci.cannotDeleteLast'));
        return;
    }
    const participant = participants.find(p => p.id === id);
    setDeleteConfirm({
      isOpen: true,
      participantId: id,
      participantName: participant?.name || ''
    });
  };

  const confirmRemoveParticipant = () => {
    const id = deleteConfirm.participantId;
    setParticipants(prev => prev.filter(p => p.id !== id));
    setMatrix(prevMatrix => {
      const newMatrix = { ...prevMatrix };
      delete newMatrix[id];
      return newMatrix;
    });
    showSuccessToast(
      t('context.raci.participantRemoved', { name: deleteConfirm.participantName || t('common.unnamed', 'Sans nom') })
    );
    setDeleteConfirm({ isOpen: false, participantId: null, participantName: '' });
  };

  const handleParticipantChange = (id, field, value) => {
     if (isTestModeActive) return; // Prevent changes in test mode

     console.log('👤 Participant change:', { id, field, value });

     setParticipants(prev => {
         const updated = prev.map(p => {
             if (p.id === id) {
                 const updatedParticipant = { ...p, [field]: value };
                 // Reset customPosition if position changes away from 'OTHER'
                 if (field === 'position' && value !== 'OTHER') {
                     updatedParticipant.customPosition = '';
                 }
                 console.log('✅ Updated participant:', updatedParticipant);
                 return updatedParticipant;
             }
             return p;
         });
         console.log('📊 All participants after update:', updated);
         return updated;
     });
  };


  // Modified to handle multiple roles per participant per workshop
  const handleRoleChange = (participantId, workshopId, role) => {
    if (isTestModeActive) return;

    setMatrix(prevMatrix => {
      const currentParticipantWorkshops = prevMatrix[participantId] || {};
      const currentRoles = currentParticipantWorkshops[workshopId] || [];
      let newRoles;

      if (currentRoles.includes(role)) {
        newRoles = currentRoles.filter(r => r !== role);
      } else {
        if (['R', 'A', 'C', 'I'].includes(role)) {
          newRoles = [...currentRoles, role].sort();
        } else {
          newRoles = currentRoles;
        }
      }

      return {
        ...prevMatrix,
        [participantId]: {
          ...currentParticipantWorkshops,
          [workshopId]: newRoles
        }
      };
    });
  };


  // Render check - show placeholder if no workshops or participants
   if (!participants || participants.length === 0) {
       return (
           <div className="text-center py-6 px-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
               <p className="text-slate-600">Aucun participant à afficher.</p>
               {!isTestModeActive && <p className="text-sm text-slate-500 mt-1">Cliquez sur "Ajouter Participant" pour commencer.</p>}
               {isTestModeActive && <p className="text-sm text-slate-500 mt-1">Les données proviennent du mode test.</p>}
           </div>
       );
   }
    if (!workshops || workshops.length === 0) {
       return (
           <div className="text-center py-6 px-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
               <p className="text-slate-600">Aucun atelier défini.</p>
                {isTestModeActive && <p className="text-sm text-slate-500 mt-1">Les ateliers proviennent du mode test.</p>}
                 {!isTestModeActive && <p className="text-sm text-slate-500 mt-1">Vérifiez la configuration ou les données chargées.</p>}
           </div>
       );
   }

  return (
    <>
      <DeleteConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, participantId: null, participantName: '' })}
        onConfirm={confirmRemoveParticipant}
        itemName={deleteConfirm.participantName}
        itemType="raciParticipant"
        title={t('context.raci.deleteParticipantTitle')}
        message={t('context.raci.deleteParticipantMessage')}
      />
      <div className="overflow-x-auto shadow-md rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
          <tr>
            <th scope="col" className="py-4 px-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider w-36 sticky left-0 bg-gradient-to-r from-slate-50 to-blue-50 z-10 border-r border-slate-200">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                {t('context.table.participant')}
              </div>
            </th>
            <th scope="col" className="py-4 px-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider w-48 border-r border-slate-200">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                {t('context.table.position')}
              </div>
            </th>
            <th scope="col" className="py-4 px-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider w-48 border-r border-slate-200">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" clipRule="evenodd" />
                </svg>
                {t('context.table.contact')}
              </div>
            </th>
            {/* Per-workshop role columns */}
            {workshops.map(workshop => (
              <th key={workshop.id} scope="col" className="py-4 px-2 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider min-w-[60px] border-r border-slate-200 whitespace-nowrap">
                <div className="flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                  </svg>
                  <span>{`A${workshop.id.slice(-1)}`}</span>
                </div>
              </th>
            ))}
            <th scope="col" className="py-4 px-4 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider w-8 sticky right-0 bg-gradient-to-r from-slate-50 to-blue-50 z-10">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {participants.map((participant, index) => (
            <tr key={participant.id} className={`hover:bg-blue-50/30 transition-colors duration-150 ${index % 2 !== 0 ? 'bg-slate-50/50' : ''}`}>
              {/* Participant Name - Sticky */}
              <td className="py-3 px-4 whitespace-nowrap text-sm text-slate-800 sticky left-0 z-0 border-r border-slate-200" style={{ backgroundColor: index % 2 !== 0 ? '#F8FAFC' /* bg-slate-50/50 */ : '#FFFFFF' /* bg-white */ }}>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className={`w-full p-2 pl-9 border rounded-md text-sm shadow-sm transition duration-150 ${isTestModeActive ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                    placeholder={t('context.fields.participantNamePlaceholder')}
                    value={participant.name || ''}
                    onChange={(e) => handleParticipantChange(participant.id, 'name', e.target.value)}
                    disabled={isTestModeActive}
                    aria-label={`Nom du participant ${index + 1}`}
                  />
                </div>
              </td>
              {/* Position / Custom Position */}
              <td className="py-3 px-4 whitespace-nowrap text-sm text-slate-800 border-r border-slate-200">
                <div className="flex flex-col space-y-2">
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <select
                      className={`w-full p-2 pl-9 border rounded-md text-sm shadow-sm transition duration-150 ${isTestModeActive ? 'bg-slate-100 border-slate-200 cursor-not-allowed appearance-none' : 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                      value={participant.position || ''}
                      onChange={(e) => handleParticipantChange(participant.id, 'position', e.target.value)}
                      disabled={isTestModeActive}
                      aria-label={`Fonction du participant ${index + 1}`}
                    >
                      <option value="" disabled>-- {t('context.fields.position')} --</option>
                      {POSITION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {participant.position === "OTHER" && (
                    <input
                      type="text"
                       className={`w-full p-2 border rounded-md text-sm shadow-sm transition duration-150 ${isTestModeActive ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : 'border-slate-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500'}`}
                      placeholder={t('context.fields.customPositionPlaceholder')}
                      value={participant.customPosition || ''}
                      onChange={(e) => handleParticipantChange(participant.id, 'customPosition', e.target.value)}
                      disabled={isTestModeActive}
                       aria-label={`Fonction personnalisée du participant ${index + 1}`}
                    />
                  )}
                </div>
              </td>
              {/* Contact */}
              <td className="py-3 px-4 whitespace-nowrap text-sm text-slate-800 border-r border-slate-200">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    className={`w-full p-2 pl-9 border rounded-md text-sm shadow-sm transition duration-150 ${isTestModeActive ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                    placeholder={t('context.fields.contactPlaceholder')}
                    value={participant.contact || ''}
                    onChange={(e) => handleParticipantChange(participant.id, 'contact', e.target.value)}
                    disabled={isTestModeActive}
                    aria-label={`Contact du participant ${index + 1}`}
                  />
                </div>
              </td>
              {/* Per-workshop role cells */}
              {workshops.map(workshop => {
                const participantRoles = matrix[participant.id]?.[workshop.id] || [];
                return (
                  <td key={workshop.id} className="py-3 px-2 whitespace-nowrap text-center border-r border-slate-200">
                    <div className="grid grid-cols-2 gap-1 justify-items-center">
                      {['R', 'A', 'C', 'I'].map(role => {
                        const isSelected = participantRoles.includes(role);

                        const selectedColors = {
                          'R': 'bg-green-500 border-green-600 text-white shadow-sm scale-110 z-10',
                          'A': 'bg-blue-500 border-blue-600 text-white shadow-sm scale-110 z-10',
                          'C': 'bg-yellow-400 border-yellow-500 text-white shadow-sm scale-110 z-10',
                          'I': 'bg-red-400 border-red-500 text-white shadow-sm scale-110 z-10'
                        };

                        return (
                          <button
                            key={role}
                            type="button"
                            onClick={() => handleRoleChange(participant.id, workshop.id, role)}
                            disabled={isTestModeActive}
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-200 border ${
                              isSelected
                                ? selectedColors[role]
                                : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600'
                            } ${isTestModeActive ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:shadow-md active:scale-95'}`}
                            title={t(`context.raci.${role === 'R' ? 'responsible' : role === 'A' ? 'accountable' : role === 'C' ? 'consulted' : 'informed'}`)}
                            aria-pressed={isSelected}
                            aria-label={`${role} - ${t(`context.raci.${role === 'R' ? 'responsible' : role === 'A' ? 'accountable' : role === 'C' ? 'consulted' : 'informed'}`)}`}
                          >
                            {role}
                          </button>
                        );
                      })}
                      {participantRoles.length === 0 && (
                        <span className="text-[10px] text-slate-400 italic">-</span>
                      )}
                    </div>
                  </td>
                );
              })}
              {/* Actions - Sticky */}
              <td className="py-3 px-4 whitespace-nowrap text-center text-sm font-medium sticky right-0 z-0" style={{ backgroundColor: index % 2 !== 0 ? '#F8FAFC' : '#FFFFFF' }}>
                 <button
                    type="button"
                    className={`p-2 rounded-full transition-all duration-200 ${
                        participants.length > 1 && !isTestModeActive
                         ? 'text-red-500 hover:text-white hover:bg-red-500 shadow-sm'
                         : 'text-slate-300 cursor-not-allowed'
                    }`}
                    onClick={() => handleRemoveParticipant(participant.id)}
                    disabled={participants.length <= 1 || isTestModeActive}
                    aria-label={`Supprimer le participant ${index + 1}`}
                 >
                    <Trash2 size={18} />
                 </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Add participant button moved to Context.js for better state management */}
    </div>
    </>
  );
};

export default ParticipantsRACIMatrix;